import { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Card, Form, Button, Table, Row, Col, Modal, Tabs, Tab } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import { useNavigate } from 'react-router-dom';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { EditorState, ContentState, convertToRaw, Modifier } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import CustomNavbar from './NavBar';

const MusicDatabase = () => {
  const [artists, setArtists] = useState<any[]>([]);
  const [artistName, setArtistName] = useState('');
  const [songTitle, setSongTitle] = useState('');
  const [songLyrics, setSongLyrics] = useState(EditorState.createEmpty());
  const [selectedArtistId, setSelectedArtistId] = useState<number | null>(null);
  const [editArtistId, setEditArtistId] = useState<number | null>(null);
  const [editSongId, setEditSongId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [showSongModal, setShowSongModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false); // New state for add modal
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'artist' | 'song' | 'chordchart', id: number } | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'split'>('table');
  const [selectedArtist, setSelectedArtist] = useState<any | null>(null);
  const [chordChartFile, setChordChartFile] = useState<File | null>(null);
  const token = localStorage.getItem('token');
  const [activeTab, setActiveTab] = useState('artist'); // Default to 'artist' tab
  const navigate = useNavigate();
  const [songKey, setSongKey] = useState<string>('');
  const musicalKeys = ['C', 'C#', 'Db', 'D', 'D#', 'Eb', 'E', 'F', 'F#', 'Gb', 'G', 'G#', 'Ab', 'A', 'A#', 'Bb', 'B'];

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchArtists();
  }, []);

  const fetchArtists = () => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/songs/artists`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setArtists(res.data);
        if (viewMode === 'split' && res.data.length > 0 && !selectedArtist) {
          setSelectedArtist(res.data[0]);
        }
      })
      .catch(err => console.error('Fetch Artists failed:', err.message));
  };

  const addArtist = () => {
    axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/songs/artists`, { name: artistName }, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    })
      .then(() => {
        setArtistName('');
        setShowAddModal(false);
        fetchArtists();
      })
      .catch(err => console.error('Add Artist failed:', err.message));
  };

  const updateArtist = () => {
    if (editArtistId) {
      axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/songs/artists/${editArtistId}`, { name: artistName }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      })
        .then(() => {
          setEditArtistId(null);
          setArtistName('');
          setShowArtistModal(false);
          fetchArtists();
        })
        .catch(err => console.error('Update Artist failed:', err.message));
    }
  };

  const deleteArtist = (id: number) => {
    setDeleteTarget({ type: 'artist', id });
    setShowDeleteModal(true);
  };

  const addSong = () => {
    if (selectedArtistId) {
      const rawContentState = convertToRaw(songLyrics.getCurrentContent());
      const lyricsHtml = draftToHtml(rawContentState);
      axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/songs/songs`,
        {
          title: songTitle,
          artistId: selectedArtistId,
          songLyrics: lyricsHtml,
          key: songKey // Include key
        },
        { headers: { Authorization: `Bearer ${token}` } })
        .then(() => {
          setSongTitle('');
          setSongLyrics(EditorState.createEmpty());
          setSelectedArtistId(null);
          setSongKey(''); // Reset key
          setShowAddModal(false);
          fetchArtists();
        })
        .catch(err => console.error('Add Song failed:', err.message));
    }
  };

  const updateSong = () => {
    if (editSongId && selectedArtistId) {
      const normalizedEditorState = applyUniformFontSize(songLyrics, '16');
      const rawContentState = convertToRaw(normalizedEditorState.getCurrentContent());
      const lyricsHtml = draftToHtml(rawContentState);
      axios
        .put(
          `${process.env.REACT_APP_API_BASE_URL}/api/songs/songs/${editSongId}`,
          {
            title: songTitle,
            artistId: selectedArtistId,
            songLyrics: lyricsHtml,
            key: songKey // Include key
          },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .then(() => {
          if (chordChartFile) {
            uploadChordChart();
          } else {
            setEditSongId(null);
            setSongTitle('');
            setSongLyrics(EditorState.createEmpty());
            setChordChartFile(null);
            setSongKey(''); // Reset key
            setShowSongModal(false);
            fetchArtists();
          }
        })
        .catch((err) => console.error('Update Song failed:', err.message));
    }
  };

  const uploadChordChart = () => {
    if (editSongId && chordChartFile) {
      const formData = new FormData();
      formData.append('file', chordChartFile);
      axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/songs/${editSongId}/chordchart`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      })
        .then(() => {
          setEditSongId(null);
          setSongTitle('');
          setSongLyrics(EditorState.createEmpty());
          setChordChartFile(null);
          setShowSongModal(false);
          fetchArtists();
        })
        .catch(err => console.error('Upload Chord Chart failed:', err.message));
    }
  };

  const deleteSong = (id: number) => {
    setDeleteTarget({ type: 'song', id });
    setShowDeleteModal(true);
  };

  const deleteChordChart = (id: number) => {
    setDeleteTarget({ type: 'chordchart', id });
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    const { type, id } = deleteTarget;
    let url = '';
    if (type === 'artist') url = `${process.env.REACT_APP_API_BASE_URL}/api/songs/artists/${id}`;
    else if (type === 'song') url = `${process.env.REACT_APP_API_BASE_URL}/api/songs/songs/${id}`;
    else if (type === 'chordchart') url = `${process.env.REACT_APP_API_BASE_URL}/api/songs/songs/${id}/chordchart`;

    axios.delete(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        fetchArtists();
        setShowDeleteModal(false);
        setDeleteTarget(null);
        if (type === 'artist' && selectedArtist?.id === id) setSelectedArtist(null);
      })
      .catch(err => console.error(`Delete ${type} failed:`, err.message));
  };

  const openChordChart = (id: number) => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/songs/${id}/chordchart`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        window.open(url, '_blank');
      })
      .catch(err => {
        console.error('Failed to open chord chart:', err.message);
        alert('Failed to open chord chart. Please check if the file exists or try again.');
      });
  };

  const getDeleteTargetName = () => {
    if (!deleteTarget) return '';
    if (deleteTarget.type === 'artist') {
      const artist = artists.find(a => a.id === deleteTarget.id);
      return artist ? artist.name : 'this artist';
    } else if (deleteTarget.type === 'song') {
      const song = artists.flatMap(a => a.songs).find(s => s.id === deleteTarget.id);
      return song ? song.title : 'this song';
    } else {
      return 'this chord chart';
    }
  };

  const openArtistEditModal = (artist: any) => {
    setEditArtistId(artist.id);
    setArtistName(artist.name);
    setShowArtistModal(true);
  };

  const openSongEditModal = (song: any, artistId: number) => {
    setEditSongId(song.id);
    setSongTitle(song.title);
    setSelectedArtistId(artistId);
    setSongKey(song.key || ''); // Set key from song data
    if (song.songLyrics) {
      const contentBlock = htmlToDraft(song.songLyrics);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        const editorState = EditorState.createWithContent(contentState);
        setSongLyrics(editorState);
      }
    } else {
      setSongLyrics(EditorState.createEmpty());
    }
    setChordChartFile(null);
    setShowSongModal(true);
  };

  const hasLyrics = (song: any) => {
    return song.songLyrics && song.songLyrics.length > 12;
  };

  const hasChordChart = (song: any) => {
    return song.chordChartPath && song.chordChartPath.length > 0;
  };

  const applyUniformFontSize = (editorState: EditorState, fontSize: string): EditorState => {
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();

    const fullSelection = selectionState.merge({
      anchorKey: contentState.getFirstBlock().getKey(),
      anchorOffset: 0,
      focusKey: contentState.getLastBlock().getKey(),
      focusOffset: contentState.getLastBlock().getLength(),
    });

    let newContentState = Modifier.removeInlineStyle(contentState, fullSelection, 'FONTSIZE');

    newContentState = Modifier.applyInlineStyle(newContentState, fullSelection, `FONTSIZE_${fontSize}`);

    const newEditorState = EditorState.push(editorState, newContentState, 'change-inline-style');
    return EditorState.forceSelection(newEditorState, fullSelection);
  };

  if (!token) return (
    <Container className="mt-5 text-center">
      <h3 className="text-light animate__animated animate__pulse" style={{ fontFamily: "'Poppins', sans-serif" }}>
        Please log in to continue
      </h3>
    </Container>
  );

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;700&display=swap');
          .modal-backdrop.show {
            opacity: 0.2 !important;
          }
          .rdw-editor-wrapper .rdw-editor-main {
            background-color: #ffffff;
            color: #000000;
            min-height: 200px;
            border: 1px solid #ccc;
            border-radius: 0 0 4px 4px;
            padding: 10px;
          }
            background: #f1f1f1;
            border: 1px solid #ccc;
            border-bottom: none;
            border-radius: 4px 4px 0 0;
            padding: 5px;
          }
          .rdw-editor-wrapper .rdw-option-wrapper, .rdw-dropdown-wrapper {
            background: #ffffff;
            color: #333333;
            border: 1px solid #ccc;
            border-radius: 4px;
          }
          .rdw-editor-wrapper .rdw-option-wrapper:hover, .rdw-dropdown-wrapper:hover {
            background: #e6e6e6;
          }
          .rdw-editor-wrapper .rdw-option-active {
            background: #d1d1d1;
          }
          .rdw-dropdown-optionwrapper {
            background: #ffffff;
            color: #333333;
          }
          .rdw-dropdown-optionwrapper:hover {
            background: #e6e6e6;
          }
          .lyrics-indicator, .chordchart-indicator {
            color: #4ecdc4;
            margin-left: 5px;
            font-size: 0.9rem;
          }
          .custom-tab-headers {
            display: flex;
            justify-content: space-between;
            margin-bottom: 1rem;
            width: 100%; /* Ensure the container takes full width */
          }
          .custom-tab-button {
            font-family: 'Montserrat', sans-serif;
            background: linear-gradient(to right, #ff6b6b, #4ecdc4);
            border: 1px solid transparent;
            color: #ffffff;
            transition: all 0.3s ease;
            padding: 0.375rem 0.75rem;
            font-size: 1rem;
            border-radius: 4px;
            flex: 1 1 0; /* Equal width for all buttons */
            margin: 0 5px;
            cursor: pointer;
            text-align: center; /* Center the text */
            min-width: 0; /* Prevent shrinking due to content */
          }
          .custom-tab-button.active {
            background: linear-gradient(to right, #e55a5a, #3db8b0);
            border: 1px solid transparent;
          }
          .custom-tab-button:hover {
            background: linear-gradient(to right, #e55a5a, #3db8b0);
            border: 1px solid transparent;
          }
        `}
      </style>
      <CustomNavbar />
      <Container fluid className="mt-5 mb-5">
        <Row className="mb-4 animate__animated animate__fadeIn">
          <Col>
            <h2 className="text-center text-light" style={{
              fontFamily: "'blowbrush', sans-serif",
              letterSpacing: '1px',
              fontSize: '3.5rem'
            }}>MUSIC DATABASE MANAGEMENT</h2>
          </Col>
        </Row>
        <Row className="animate__animated animate__fadeIn" style={{ animationDelay: '0.4s' }}>
          <Col>
            <Card className="card-custom">
              <Card.Header
                as="h3"
                style={{ background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)', color: 'white', fontFamily: "'Poppins', sans-serif'", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                Artists & Songs
                <div>
                  <Button
                    variant="outline-light"
                    onClick={() => setViewMode('table')}
                    style={{ fontFamily: "'Montserrat', sans-serif'", borderColor: '#ffffff', color: '#ffffff', transition: 'all 0.3s ease', padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; e.currentTarget.style.color = '#ff6b6b'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#ffffff'; }}
                  >
                    Table View
                  </Button>
                  <Button
                    variant="outline-light"
                    onClick={() => setViewMode('split')}
                    style={{ fontFamily: "'Montserrat', sans-serif'", borderColor: '#ffffff', color: '#ffffff', transition: 'all 0.3s ease', padding: '0.25rem 0.5rem', fontSize: '0.875rem', marginLeft: '10px' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; e.currentTarget.style.color = '#ff6b6b'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#ffffff'; }}
                  >
                    Split View
                  </Button>
                  <Button
                    variant="outline-light"
                    onClick={() => setShowAddModal(true)}
                    style={{ fontFamily: "'Montserrat', sans-serif'", borderColor: '#ffffff', color: '#ffffff', transition: 'all 0.3s ease', padding: '0.25rem 0.5rem', fontSize: '0.875rem', marginLeft: '10px' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; e.currentTarget.style.color = '#ff6b6b'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#ffffff'; }}
                  >
                    Add Artists & Songs
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {viewMode === 'table' ? (
                  <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <Table striped bordered hover className="table-custom">
                      <thead>
                        <tr>
                          <th>Artist</th>
                          <th>Songs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {artists.map((artist, index) => (
                          <tr key={artist.id} className="animate__animated animate__slideInUp" style={{ animationDelay: `${0.1 * index}s` }}>
                            <td>
                              {artist.name}
                              <Button variant="link" size="sm" onClick={() => openArtistEditModal(artist)} style={{ color: '#d63384', marginLeft: '10px' }}>Edit</Button>
                              <Button variant="link" size="sm" className="text-danger" onClick={() => deleteArtist(artist.id)}>Delete</Button>
                            </td>
                            <td>
                              <ul>
                                {artist.songs.map((song: any) => (
                                  <li key={song.id}>
                                    {song.title}
                                    {hasLyrics(song) && (
                                      <span className="lyrics-indicator" title="Lyrics Added">🎵</span>
                                    )}
                                    {hasChordChart(song) && (
                                      <span className="chordchart-indicator" title="Chord Chart Added">🎸</span>
                                    )}
                                    <Button variant="link" size="sm" onClick={() => openSongEditModal(song, artist.id)} style={{ color: '#d63384' }}>Edit</Button>
                                    <Button variant="link" size="sm" className="text-danger" onClick={() => deleteSong(song.id)}>Delete</Button>
                                  </li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div style={{ display: 'flex', maxHeight: '70vh', overflow: 'hidden' }}>
                    <div style={{ width: '30%', overflowY: 'auto', borderRight: '1px solid #ddd', padding: '10px' }}>
                      {artists.map((artist, index) => (
                        <div
                          key={artist.id}
                          onClick={() => setSelectedArtist(artist)}
                          className={`p-2 mb-2 animate__animated animate__fadeIn`}
                          style={{
                            cursor: 'pointer',
                            fontFamily: "'Montserrat', sans-serif",
                            transition: 'background-color 0.3s ease, color 0.3s ease',
                            backgroundColor: selectedArtist?.id === artist.id ? '#d63384' : 'transparent',
                            color: selectedArtist?.id === artist.id ? '#ffffff' : '#000000',
                          }}
                          onMouseEnter={(e) => {
                            if (selectedArtist?.id !== artist.id) e.currentTarget.style.backgroundColor = '#f0f0f0';
                            else e.currentTarget.style.backgroundColor = '#b0266b';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = selectedArtist?.id === artist.id ? '#d63384' : 'transparent';
                            e.currentTarget.style.color = selectedArtist?.id === artist.id ? '#ffffff' : '#000000';
                          }}
                        >
                          {artist.name}
                          <Button
                            variant="link"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openArtistEditModal(artist); }}
                            style={{ color: selectedArtist?.id === artist.id ? '#ffffff' : '#d63384', marginLeft: '10px' }}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className={selectedArtist?.id === artist.id ? 'text-light' : 'text-danger'}
                            onClick={(e) => { e.stopPropagation(); deleteArtist(artist.id); }}
                          >
                            Delete
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div style={{ width: '70%', overflowY: 'auto', padding: '10px' }}>
                      {selectedArtist ? (
                        <Table striped bordered hover className="table-custom">
                          <thead>
                            <tr>
                              <th>Song</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedArtist.songs.map((song: any) => (
                              <tr key={song.id}>
                                <td>
                                  {song.title}
                                  {hasLyrics(song) && (
                                    <span className="lyrics-indicator" title="Lyrics Added">🎵</span>
                                  )}
                                  {hasChordChart(song) && (
                                    <span className="chordchart-indicator" title="Chord Chart Added">🎸</span>
                                  )}
                                </td>
                                <td>
                                  <Button variant="link" size="sm" onClick={() => openSongEditModal(song, selectedArtist.id)} style={{ color: '#d63384' }}>Edit</Button>
                                  <Button variant="link" size="sm" className="text-danger" onClick={() => deleteSong(song.id)}>Delete</Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <p className="text-center text-muted" style={{ fontFamily: "'Montserrat', sans-serif" }}>Select an artist to view songs</p>
                      )}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="card-custom">
        <Modal.Header closeButton style={{ background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)', color: 'white', fontFamily: "'Poppins', sans-serif'", border: 'none' }}>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontFamily: "'Montserrat', sans-serif'", backgroundColor: '#2c2c2c', color: 'white' }}>
          Are you sure you want to delete "{getDeleteTargetName()}"?
          {deleteTarget?.type === 'artist' && ' This will also delete all associated songs and chord charts.'}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#2c2c2c', borderTop: 'none' }}>
          <Button
            variant="outline-light"
            onClick={() => setShowDeleteModal(false)}
            style={{ fontFamily: "'Montserrat', sans-serif'", borderColor: '#ff6b6b', color: '#ff6b6b', transition: 'all 0.3s ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#ff6b6b'; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#ff6b6b'; }}
          >
            Cancel
          </Button>
          <Button
            className="btn-custom-primary"
            onClick={confirmDelete}
            style={{ fontFamily: "'Montserrat', sans-serif'", backgroundColor: '#d63384', borderColor: '#d63384', transition: 'all 0.3s ease' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#b0266b'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#d63384'}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Artist Modal */}
      <Modal show={showArtistModal} onHide={() => setShowArtistModal(false)} centered className="card-custom">
        <Modal.Header closeButton style={{ background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)', color: 'white', fontFamily: "'Poppins', sans-serif'", border: 'none' }}>
          <Modal.Title>Edit Artist</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontFamily: "'Montserrat', sans-serif'", backgroundColor: '#2c2c2c', color: 'white' }}>
          <Form.Group className="mb-3">
            <Form.Label>Artist Name</Form.Label>
            <Form.Control type="text" value={artistName} onChange={e => setArtistName(e.target.value)} className="shadow-sm" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#2c2c2c', borderTop: 'none' }}>
          <Button
            variant="outline-light"
            onClick={() => setShowArtistModal(false)}
            style={{ fontFamily: "'Montserrat', sans-serif'", borderColor: '#ff6b6b', color: '#ff6b6b', transition: 'all 0.3s ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#ff6b6b'; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#ff6b6b'; }}
          >
            Cancel
          </Button>
          <Button
            className="btn-custom-primary"
            onClick={updateArtist}
            disabled={!artistName}
            style={{ fontFamily: "'Montserrat', sans-serif'", backgroundColor: '#d63384', borderColor: '#d63384', transition: 'all 0.3s ease' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#b0266b'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#d63384'}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Song Modal */}
      <Modal show={showSongModal} onHide={() => setShowSongModal(false)} centered className="card-custom">
        <Modal.Header closeButton style={{ background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)', color: 'white', fontFamily: "'Poppins', sans-serif'", border: 'none' }}>
          <Modal.Title>Edit Song</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontFamily: "'Montserrat', sans-serif'", backgroundColor: '#2c2c2c', color: 'white' }}>
          <Form.Group className="mb-3">
            <Form.Label>Song Title</Form.Label>
            <Form.Control type="text" value={songTitle} onChange={e => setSongTitle(e.target.value)} className="shadow-sm" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Artist</Form.Label>
            <Form.Select value={selectedArtistId || ''} onChange={e => setSelectedArtistId(Number(e.target.value))} className="shadow-sm">
              {artists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Key</Form.Label>
            <Form.Select value={songKey} onChange={e => setSongKey(e.target.value)} className="shadow-sm">
              <option value="">Select Key</option>
              {musicalKeys.map(key => <option key={key} value={key}>{key}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Lyrics</Form.Label>
            <Editor
              editorState={songLyrics}
              onEditorStateChange={setSongLyrics}
              wrapperClassName="rdw-editor-wrapper"
              editorClassName="rdw-editor-main"
              toolbarClassName="rdw-editor-toolbar"
              toolbar={{
                options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'colorPicker', 'link', 'emoji', 'remove', 'history'],
                inline: { options: ['bold', 'italic', 'underline', 'strikethrough'] },
                list: { options: ['unordered', 'ordered'] },
              }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Chord Chart (PDF)</Form.Label>
            <Form.Control
              type="file"
              accept=".pdf"
              onChange={(e: any) => setChordChartFile(e.target.files[0])}
              className="shadow-sm"
            />
            {editSongId && hasChordChart(artists.flatMap(a => a.songs).find(s => s.id === editSongId)) && (
              <div className="mt-2">
                <Button variant="link" onClick={() => openChordChart(editSongId)} style={{ color: '#4ecdc4' }}>View Current Chord Chart</Button>
                <Button variant="link" className="text-danger" onClick={() => deleteChordChart(editSongId)}>Delete Chord Chart</Button>
              </div>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#2c2c2c', borderTop: 'none' }}>
          <Button
            variant="outline-light"
            onClick={() => setShowSongModal(false)}
            style={{ fontFamily: "'Montserrat', sans-serif'", borderColor: '#ff6b6b', color: '#ff6b6b', transition: 'all 0.3s ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#ff6b6b'; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#ff6b6b'; }}
          >
            Cancel
          </Button>
          <Button
            className="btn-custom-primary"
            onClick={updateSong}
            disabled={!songTitle || !selectedArtistId}
            style={{ fontFamily: "'Montserrat', sans-serif'", backgroundColor: '#d63384', borderColor: '#d63384', transition: 'all 0.3s ease' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#b0266b'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#d63384'}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Artist & Song Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered className="card-custom">
        <Modal.Header closeButton style={{ background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)', color: 'white', fontFamily: "'Poppins', sans-serif'", border: 'none' }}>
          <Modal.Title>Add Artists & Songs</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontFamily: "'Montserrat', sans-serif'", backgroundColor: '#2c2c2c', color: 'white' }}>
          <div className="custom-tab-headers mb-3">
            <button
              className={`custom-tab-button ${activeTab === 'artist' ? 'active' : ''}`}
              onClick={() => setActiveTab('artist')}
            >
              Add Artist
            </button>
            <button
              className={`custom-tab-button ${activeTab === 'song' ? 'active' : ''}`}
              onClick={() => setActiveTab('song')}
            >
              Add Song
            </button>
          </div>
          <div className="custom-tab-content">
            {activeTab === 'artist' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Artist Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Artist Name"
                    value={artistName}
                    onChange={e => setArtistName(e.target.value)}
                    className="shadow-sm"
                  />
                </Form.Group>
                <Button
                  className="btn-custom-primary"
                  onClick={addArtist}
                  disabled={!artistName}
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(to right, #e55a5a, #3db8b0)';
                    e.currentTarget.style.border = '1px solid transparent';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(to right, #ff6b6b, #4ecdc4)';
                    e.currentTarget.style.border = '1px solid transparent';
                  }}
                >
                  Add Artist
                </Button>
              </>
            )}
            {activeTab === 'song' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Artist</Form.Label>
                  <Form.Select
                    onChange={e => setSelectedArtistId(Number(e.target.value))}
                    value={selectedArtistId || ''}
                    className="shadow-sm"
                  >
                    <option value="">Select Artist</option>
                    {artists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Song Title</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Song Title"
                    value={songTitle}
                    onChange={e => setSongTitle(e.target.value)}
                    disabled={!selectedArtistId}
                    className="shadow-sm"
                  />
                </Form.Group>
                <Button
                  className="btn-custom-primary"
                  onClick={addSong}
                  disabled={!songTitle || !selectedArtistId}
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(to right, #e55a5a, #3db8b0)';
                    e.currentTarget.style.border = '1px solid transparent';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(to right, #ff6b6b, #4ecdc4)';
                    e.currentTarget.style.border = '1px solid transparent';
                  }}
                >
                  Add Song
                </Button>
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#2c2c2c', borderTop: 'none' }}>
          <Button
            variant="outline-light"
            onClick={() => setShowAddModal(false)}
            style={{ fontFamily: "'Montserrat', sans-serif'", borderColor: '#ff6b6b', color: '#ff6b6b', transition: 'all 0.3s ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#ff6b6b'; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#ff6b6b'; }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MusicDatabase;